import React, { useCallback, useMemo } from 'react';
import { compact, findIndex, get } from 'lodash';
import type { NamedEntity, ReturnCell, RollingCorrelationResponse } from 'venn-api';
import { useRecoilValue } from 'recoil';
import type { AnalysisRequest } from '../../types';
import {
  assertNotNil,
  convertMultipleSeriesDataToExcel,
  getStrategyNameForFilteredSubject,
  hasFeesExcluded,
} from 'venn-utils';
import type { GeneralChartProps, Serie } from './types';
import LineChart from './LineChart';
import BarChart from './BarChart';
import { DownloadableContentBlock } from '../../../content-block';
import useExportUpdate from '../../logic/useExportUpdate';
import { WATERMARK_POSITION_TOP } from '../../customAnalysisContants';
import StyledEmptyState from '../StyledEmptyState';
import { formatExportableNameWithOptionalFee, formatNameWithOptionalFee } from '../../../legend';
import { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';
import { useBlockId } from '../../contexts/BlockIdContext';
import { blockSubjectHasProxyState } from 'venn-state';
import {
  useGetBlockSubjectSuperscripts,
  usePageProxySymbol,
} from '../../../blocks/components/core/block-subject-footnotes-and-superscripts';
import { useGetRequestSubjectFee } from '../../../blocks/utils/hooks/use-advisory-fee';

const RollingCorrelationBlock = ({
  metrics,
  selectedBlock,
  analyses,
  requests,
  downloadableContentRef,
  height,
  selectedRefId,
  exportMetaData,
  inPrintMode,
}: GeneralChartProps) => {
  const metric = metrics ? metrics[0] : undefined;
  const blockId = useBlockId();
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(blockId));
  const proxySymbol = usePageProxySymbol();
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const isExportable = (exportable: boolean[], reqs: AnalysisRequest[], seriesName: NamedEntity) => {
    const index = findIndex(reqs, (req) => req.subject.id === String(seriesName.id));
    if (index === -1) {
      return false;
    }
    return exportable[index];
  };

  const [series, exportableSeries] = useMemo(() => {
    if (!analyses || analyses.length < 1 || !metric) {
      return [[], []];
    }

    const series: Serie[] = [];
    const exportableSeries: Serie[] = [];

    const analysis = compact(analyses)[0]!.find((analysis) => analysis?.analysisType === 'ROLLING_CORRELATION');
    const exportable = analysis?.exportable;

    analysis?.rollingCorrelation.forEach((correlation: RollingCorrelationResponse) => {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const ts = metric.metricPath ? get(correlation, metric.metricPath) : correlation[0];
      if (!ts) {
        return;
      }

      // TODO: VENN-24861 Fix subject and subject name logic.
      const primarySubjectName = correlation.subjectName.name; // getSubjectLabelForChart(correlation.subjectName);
      const secondarySubjectName = correlation.secondarySubjectName.name; // getSubjectLabelForChart(correlation.secondarySubjectName);
      const primaryRequest = assertNotNil(
        requests.find((request) => {
          return request.subject.name === primarySubjectName;
        }),
      );
      const primarySubject = primaryRequest.subject;
      const secondaryRequest = requests.find((request) => {
        return request.subject.name === secondarySubjectName;
      });
      const secondarySubject = requests.find((request) => {
        return request.subject.name === secondarySubjectName;
      })?.subject;
      const primarySubjectFee = getRequestSubjectFee(primaryRequest);
      const secondarySubjectFee = secondaryRequest ? getRequestSubjectFee(secondaryRequest) : undefined;
      const seriesName = `${formatNameWithOptionalFee(
        primarySubjectName,
        proxySymbol,
        getBlockSubjectSuperscripts({
          subject: primarySubject,
          isBenchmark: !!primarySubject.individualBenchmark?.benchmark,
        }),
        primarySubjectFee,
        !!subjectHasProxy[primarySubject.id],
        getStrategyNameForFilteredSubject(primarySubject),
      )} with ${formatNameWithOptionalFee(
        secondarySubjectName,
        proxySymbol,
        secondarySubject
          ? getBlockSubjectSuperscripts({
              subject: secondarySubject,
              isBenchmark: !!secondarySubject.individualBenchmark?.benchmark,
            })
          : '',
        secondarySubjectFee,
        correlation?.secondarySubjectName?.id ? !!subjectHasProxy[correlation.secondarySubjectName.id] : undefined,
        secondarySubject ? getStrategyNameForFilteredSubject(secondarySubject) : undefined,
      )}`;
      const exportableSeriesName = `${formatExportableNameWithOptionalFee(
        primarySubjectName,
        primarySubjectFee,
        hasFeesExcluded(primarySubject),
        undefined,
        undefined,
      )} with ${formatExportableNameWithOptionalFee(
        secondarySubjectName,
        secondarySubjectFee,
        secondarySubject ? hasFeesExcluded(secondarySubject) : undefined,
        undefined,
        undefined,
      )}`;
      const seriesData = convertToTimeseries(ts);
      const serie = {
        name: seriesName,
        data: seriesData,
      };
      const exportableSerie = {
        name: exportableSeriesName,
        data: seriesData,
      };
      series.push(serie);

      if (
        isExportable(exportable ?? [], requests, correlation.subjectName) &&
        isExportable(exportable ?? [], requests, correlation.secondarySubjectName)
      ) {
        exportableSeries.push(exportableSerie);
      }
    });
    return [series, exportableSeries];
  }, [analyses, getBlockSubjectSuperscripts, getRequestSubjectFee, metric, proxySymbol, requests, subjectHasProxy]);

  const defaultHeader =
    selectedBlock?.relativeToBenchmark && metric?.relativeLabel
      ? metric.relativeLabel
      : !metric
        ? 'Builder Chart'
        : metric.label;

  const excelDataFn = useCallback(
    () =>
      convertMultipleSeriesDataToExcel(
        exportableSeries.map((s) => ({ name: s.name, series: s.data })),
        metric?.dataType !== 'NUMERIC',
      ),
    [exportableSeries, metric?.dataType],
  );

  useExportUpdate({ exportMetaData, excelDataFn, selectedRefId });

  const Component = selectedBlock?.infoGraphicType === 'LINE' ? LineChart : BarChart;

  const component =
    series.length > 0 ? (
      <Component
        start={requests?.[0]?.dateRange.from}
        end={requests?.[0]?.dateRange.to}
        series={series}
        frequency={requests?.[0]?.frequency ?? 'MONTHLY'}
        yAxisUnitFormat={metric?.dataType === 'NUMERIC' ? 'ratio' : 'percent'}
        inPrintMode={inPrintMode}
        height={height}
      />
    ) : (
      <StyledEmptyState selectedRefId={selectedRefId} header="Unable to run analysis" message="Chart is empty" />
    );

  if (inPrintMode) {
    return component;
  }

  return (
    <DownloadableContentBlock
      header={<EmptyHeaderSpace />}
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: selectedBlock?.header ? selectedBlock?.header : defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark: !!selectedBlock?.relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: series.length === 0,
        target: downloadableContentRef,
      }}
    >
      {component}
    </DownloadableContentBlock>
  );
};

export default RollingCorrelationBlock;

const convertToTimeseries = (ts: number[][] | ReturnCell[]): number[][] => {
  if (!ts) {
    return [];
  }
  if (Array.isArray(ts[0])) {
    return ts as number[][];
  }

  return (ts as ReturnCell[]).map((t) => [t.date, t.val]);
};
